<template>
  <div class="list-page">
    <div class="card">
      <div class="card-head">
        <el-button icon="el-icon-arrow-left" @click="goBack">
          {{ $t("actions.back") }}
        </el-button>
        <h2 class="title">{{ pageTitle }}</h2>
        <div class="spacer"></div>
        <el-button size="small" type="primary" plain @click="goStatistics">
          {{ isEn ? 'View All Statistics' : '查看所有统计结果' }}
        </el-button>
        <el-input
          v-model="keyword"
          size="small"
          clearable
          :placeholder="$t('result.searchPlaceholder')"
          class="search"
        />
        <el-button
          size="small"
          type="primary"
          icon="el-icon-refresh"
          :loading="loading"
          @click="fetchPatients"
        >
          {{ $t("actions.refresh") }}
        </el-button>
      </div>

      <el-table
        :data="filteredRows"
        stripe
        border
        size="small"
        v-loading="loading"
        style="width: 100%"
        @row-dblclick="viewDetail"
      >
        <el-table-column prop="patient" :label="$t('fields.patient')" min-width="180" />
        <el-table-column prop="date" :label="$t('fields.date')" width="140" />
        <el-table-column :label="$t('actions.action')" width="220" align="center">
          <template slot-scope="scope">
            <el-button type="primary" size="mini" @click.stop="viewDetail(scope.row)">
              {{ $t("actions.viewResult") }}
            </el-button>
            <el-button type="danger" size="mini" @click.stop="deleteRow(scope.row)">
              {{ isEn ? 'Delete' : '删除' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-empty
        v-if="!loading && filteredRows.length === 0"
        :description="$t('result.noMatch')"
      />
    </div>
  </div>
</template>

<script>
import { listPatients, deletePatient } from "@/api";

export default {
  name: "ResultsList",
  data() {
    return {
      loading: false,
      rows: [],
      keyword: "",
      loadedOwnerName: "",
    };
  },
  created() {
    this.fetchPatients();
  },
  watch: {
    ownerId() {
      this.fetchPatients();
    },
  },
  computed: {
    isEn() {
      return this.$i18n.locale === "en";
    },
    ownerId() {
      return this.$route.query.owner_id || null;
    },
    ownerName() {
      return this.$route.query.owner_name || this.loadedOwnerName || this.ownerId;
    },
    pageTitle() {
      if (!this.ownerId) return this.$t("result.processedList");
      return this.isEn ? `${this.ownerName}'s Data` : `${this.ownerName} 的所有数据`;
    },
    filteredRows() {
      const kw = (this.keyword || "").trim();
      if (!kw) return this.rows;
      return this.rows.filter(
        (r) => r.patient.includes(kw) || r.date.includes(kw)
      );
    },
  },
  methods: {
    async fetchPatients() {
      this.loading = true;
      try {
        const res = await listPatients(this.ownerId);
        const list = res && res.data && res.data.patients ? res.data.patients : [];
        this.loadedOwnerName = (res && res.data && res.data.owner_username) || "";
        this.rows = list.map((item) => {
          const i = item.lastIndexOf("-");
          return {
            display: item,
            patient: item.slice(0, i),
            date: item.slice(i + 1),
          };
        });
      } catch (e) {
        this.$message.error(this.$t("messages.fetchFail"));
      } finally {
        this.loading = false;
      }
    },
    goStatistics() {
      const query = { scope: "all" };
      if (this.ownerId) query.owner_id = this.ownerId;
      if (this.ownerName) query.owner_name = this.ownerName;
      this.$router.push({ path: "/results/statistics", query });
    },
    viewDetail(row) {
      const query = { source: "management" };
      if (this.ownerId) query.owner_id = this.ownerId;
      if (this.ownerName) query.owner_name = this.ownerName;
      this.$router.push({
        path: `/results/${row.patient}/${row.date}`,
        query,
      });
    },
    async deleteRow(row) {
      try {
        await this.$confirm(
          this.isEn
            ? `Delete all data for ${row.patient} · ${row.date}?`
            : `确定删除 ${row.patient}·${row.date} 的全部数据吗？`,
          this.isEn ? "Notice" : "提示",
          { type: "warning" }
        );
        if (this.ownerId) {
          const base = process.env.VUE_APP_BASE_URL || "http://localhost:4201";
          await this.$http.delete(
            `${base}/admin/patients/${encodeURIComponent(row.patient)}/${row.date}?user_id=${encodeURIComponent(this.ownerId)}`
          );
        } else {
          await deletePatient(row.patient, row.date);
        }
        this.$message.success(this.isEn ? "Deleted" : "已删除");
        await this.fetchPatients();
      } catch (e) {
        if (e !== "cancel") {
          this.$message.error(e.response?.data?.detail || (this.isEn ? "Delete failed" : "删除失败"));
        }
      }
    },
    goBack() {
      this.$router.push("/");
    },
  },
};
</script>

<style scoped>
.list-page {
  width: clamp(960px, 86vw, 1140px);
  margin: 24px auto 80px;
}

.card {
  background: #ffffff;
  border-radius: 14px;
  padding: 14px 16px 18px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
}

.card-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.title {
  margin: 0 4px;
  color: #0f172a;
  font-weight: 800;
}

.spacer {
  flex: 1;
}

.search {
  width: 240px;
}

:deep(.el-table th) {
  background: #f8fafc;
  color: #0f172a;
  font-weight: 700;
}

:deep(.el-table .cell) {
  color: #111827;
}
</style>
